import { Plus, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"

// Generic dynamic list renderer. `render` receives (item, updateField, removeItem).
export function DynamicList({
  items,
  onChange,
  min = 1,
  max = 7,
  render,
  addLabel = "Add another",
  newItem = () => ({ text: "" }),
}) {
  const updateItem = (idx, next) => {
    const updated = items.map((item, i) => (i === idx ? next : item))
    onChange(updated)
  }

  const addItem = () => {
    if (items.length >= max) return
    onChange([...items, newItem()])
  }

  const removeItem = (idx) => {
    if (items.length <= min) return
    onChange(items.filter((_, i) => i !== idx))
  }

  return (
    <div className="space-y-3">
      {items.map((item, idx) => (
        <div key={item.id || idx} className="flex items-start gap-2">
          <div className="flex-1">{render(item, (field, value) => updateItem(idx, { ...item, [field]: value }))}</div>
          {items.length > min && (
            <button
              type="button"
              onClick={() => removeItem(idx)}
              aria-label="Remove"
              className="p-2 text-zinc-400 hover:text-kf-red"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ))}

      {items.length < max && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={addItem}
          className="text-kf-red"
        >
          <Plus className="w-4 h-4 mr-1" />
          {addLabel}
        </Button>
      )}
    </div>
  )
}

// Simple text-field row — use for open-ended list items (goals, issues).
export function TextFieldRow({ value, onChange, placeholder }) {
  return (
    <Textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={2}
      className="resize-none"
    />
  )
}

// KPI/KOI row — name + target
export function KpiFieldRow({ item, update, namePlaceholder, targetPlaceholder }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      <Input
        placeholder={namePlaceholder}
        value={item.name || ""}
        onChange={(e) => update("name", e.target.value)}
        className="sm:col-span-2"
      />
      <Input
        placeholder={targetPlaceholder}
        value={item.target || ""}
        onChange={(e) => update("target", e.target.value)}
      />
    </div>
  )
}
